import { getISOWeek, getISOWeekYear } from 'date-fns';

export function getIsoWeek(date: Date | string): string {
  const target = new Date(date.toString());
  const weekNumber = getISOWeek(target);
  return weekNumber.toString().padStart(2, '0');
}

export function getCalendarWeek(date: Date): string {
  const isoYear = getISOWeekYear(date);
  const week = getISOWeek(date);
  return `${isoYear} CW${week.toString().padStart(2, '0')}`;
}

/** Calendar date key YYYY-MM-DD from API string — no timezone shift. */
export function isoDateKey(value: string): string {
  return value.trim().slice(0, 10);
}

/** Calendar date key from a local Date (day picker click). */
export function dateToIsoKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse YYYY-MM-DD as local midnight (not UTC). */
export function isoKeyToLocalDate(key: string): Date {
  const [y, m, d] = isoDateKey(key).split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function isoKeyYear(key: string): number {
  return Number.parseInt(isoDateKey(key).slice(0, 4), 10);
}

/**
 * The application-wide date display format: dd.mm.yyyy.
 *
 * Date-only strings (YYYY-MM-DD) are sliced textually rather than parsed via
 * `new Date()`, which would shift the day across timezones.
 */
export function formatDateDdMmYyyy(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  const s = String(value).trim();
  let y: number;
  let m: number;
  let day: number;
  if (s.length >= 10 && s[4] === '-' && s[7] === '-') {
    y = Number(s.slice(0, 4));
    m = Number(s.slice(5, 7));
    day = Number(s.slice(8, 10));
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(day)) {
      return '—';
    }
  } else {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '—';
    y = d.getFullYear();
    m = d.getMonth() + 1;
    day = d.getDate();
  }
  if (!y || !m || !day) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(day)}.${pad(m)}.${y}`;
}

/**
 * Timestamp display: dd.mm.yyyy HH:mm in local time. Use for audit columns
 * where the time of day matters; use formatDateDdMmYyyy for calendar dates.
 */
export function formatDateTimeDdMmYyyy(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  const d = new Date(String(value).trim());
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
